import { useEffect, useState } from 'react'
import useSWR from 'swr'
import spreadKeyFetcher from './spreadKeyFetcher'
import useRequestPage from './useRequestPage'

const useSWRFetchPagination = (fetcher, param, fetcherOptions, currentPage = 1, perPage = 5) => {
  const [meta, setMeta] = useState({
    total: null,
    currentPage,
    perPage,
    from: null,
    to: null,
    lastPage: null
  })

  const [data, setData] = useState([])
  const requestPage = useRequestPage(param, meta.currentPage, () => {
    setMeta((prev) => ({ ...prev, currentPage: 1 }))
  })

  const { data: dataResult, error, mutate } = useSWR([requestPage, meta.perPage, param], spreadKeyFetcher(fetcher), fetcherOptions)

  useEffect(() => {
    if (dataResult?.meta) {
      // El backend devuelve estos campos como texto cuando vienen de
      // vuelta del query string (ej. "per_page":"5" en vez de 5). Si no se
      // normalizan a número acá, la clave de useSWR de abajo
      // ([currentPage, perPage, param]) cambia de tipo (5 -> "5") en el
      // primer refresh, SWR lo interpreta como una clave nueva y vuelve a
      // pedir los datos — y como la respuesta siempre trae el mismo texto,
      // queda repitiéndose sin parar.
      setMeta((prev) => {
        const rawPerPage = dataResult.meta.per_page ?? dataResult.meta.perPage;
        const parsedPerPage = Number(rawPerPage);
        const validPerPage = !isNaN(parsedPerPage) && parsedPerPage > 0 ? parsedPerPage : (prev.perPage || perPage || 5);
        return {
          total: Number(dataResult.meta.total ?? prev.total),
          currentPage: Number(dataResult.meta.current_page ?? prev.currentPage),
          lastPage: Number(dataResult.meta.last_page ?? prev.lastPage),
          perPage: validPerPage,
          to: dataResult.meta.to,
          from: dataResult.meta.from
        };
      })
    }
    if (dataResult?.data) {
      if (fetcherOptions?.ASC) {
        setData(dataResult.data.reverse())
      } else {
        setData(dataResult.data)
      }
    }
    // eslint-disable-next-line
    }, [dataResult, setData, setMeta]);

  const handleNextPage = () => { setMeta(prevs => { return { ...prevs, currentPage: prevs.currentPage + 1 } }) }

  const handlePreviousPage = () => { setMeta(prevs => { return { ...prevs, currentPage: (prevs.currentPage - 1) <= 1 ? 1 : (prevs.currentPage - 1) } }) }

  const handleGoToPage = (pageNumber) => { setMeta(prevs => { return { ...prevs, currentPage: pageNumber } }) }

  const handlePerPage = (perPageNumber) => { setMeta(prevs => { return { ...prevs, perPage: perPageNumber } }) }

  return {
    data,
    meta,
    isLoading: !error && !dataResult,
    error,
    mutate,
    handler: {
      nextPage: handleNextPage,
      previousPage: handlePreviousPage,
      goToPage: handleGoToPage,
      perPage: handlePerPage
    }
  }
}

export default useSWRFetchPagination
